import * as THREE from 'three';
import {
    isFeatureFace,
    isFeatureLine,
    isFeaturePoint,
    FeaturePreviewMesh,
    FeatureResultMesh,
    isFeaturePreviewMesh,
    isFeatureResultMesh,
} from '../../mesh/feature';

/**
 * 读取拓扑代理对象显式绑定的结果体。
 *
 * 设计说明：
 * 1. `FeatureFace` 在结果体三角命中后可能以“脱离场景父子树”的临时代理形式存在；
 * 2. 如果仍然只依赖 `parent` 向上查找，web-client 将无法把选中的面代理还原为正式结果体；
 * 3. 因此这里先消费拓扑代理显式持有的结果体归属，再回退到场景父子树遍历。
 *
 * @param object 场景对象。
 * @returns 显式绑定的结果体；缺失时返回 `null`。
 */
function resolveExplicitOwningResultMesh(
    object: THREE.Object3D | null | undefined,
): FeatureResultMesh | null {
    if (!object) {
        return null;
    }
    if (isFeatureFace(object) || isFeatureLine(object) || isFeaturePoint(object)) {
        const getter = Reflect.get(object, 'getOwningResultMesh');
        if (typeof getter === 'function') {
            const owner = getter.call(object);
            return isFeatureResultMesh(owner) ? owner : null;
        }
    }
    return null;
}

/**
 * 判断对象是否位于特征预览体树下。
 *
 * 设计说明：
 * 1. 预览体只负责临时显示，不得参与正式选择、提交或编辑链；
 * 2. 这里统一在 cad-core 底层识别预览体祖先，避免 web-client 各处自行过滤；
 * 3. 后续 Draft/Shell/Move Face 等特征的预览对象也可复用同一条语义边界。
 *
 * @param object 场景对象。
 * @returns 是否位于预览体树下。
 */
export function isObjectInsideFeaturePreviewMesh(
    object: THREE.Object3D | null | undefined,
): boolean {
    let current = object ?? null;
    while (current) {
        if (isFeaturePreviewMesh(current)) {
            return true;
        }
        current = current.parent;
    }
    return false;
}

/**
 * 解析对象所属的正式结果体。
 *
 * 设计说明：
 * 1. 只有正式结果体才可以作为特征输入的上游实体来源；
 * 2. 一旦命中链路上出现预览体祖先，应立即终止解析并返回 `null`；
 * 3. 这样可以从底层杜绝“预览体对象 ID 污染提交参数”的问题。
 *
 * @param object 场景对象。
 * @returns 正式结果体；若对象位于预览体树下则返回 `null`。
 */
export function resolveOwningFeatureResultMesh(
    object: THREE.Object3D | null | undefined,
): FeatureResultMesh | null {
    const explicitOwner = resolveExplicitOwningResultMesh(object);
    if (explicitOwner) {
        return explicitOwner;
    }
    let current = object ?? null;
    while (current) {
        if (isFeaturePreviewMesh(current)) {
            return null;
        }
        if (isFeatureResultMesh(current)) {
            return current;
        }
        current = current.parent;
    }
    return null;
}

/**
 * 解析对象所属的预览体。
 *
 * @param object 场景对象。
 * @returns 预览体；不存在时返回 `null`。
 */
export function resolveOwningFeaturePreviewMesh(
    object: THREE.Object3D | null | undefined,
): FeaturePreviewMesh | null {
    let current = object ?? null;
    while (current) {
        if (isFeaturePreviewMesh(current)) {
            return current;
        }
        current = current.parent;
    }
    return null;
}
